import { action, computed, observable } from 'mobx';
import { messageActions } from '../action';

const LineCountLimit: number = 2000;

export interface FullTerminal {
  currentAutocompleteCommand: string;
  descriptor: TerminalInfo.Terminal;
  lines: TerminalInfo.Line[];
  endOfCommand: boolean;
  history: string[];
  currentCommand: string;
  currentHistoryIndex: number;
}

function createFullTerminal(descriptor: TerminalInfo.Terminal): FullTerminal {
  return {
    lines: [],
    descriptor,
    endOfCommand: true,
    history: [],
    currentCommand: '',
    currentHistoryIndex: 0,
    currentAutocompleteCommand: '',
  };
}

export interface TerminalState {
  waitingForNewTerminal: boolean;
  terminals: { [key: string]: FullTerminal };
  orderedTerminals: FullTerminal[];
}

export class TerminalStore {
  @observable private terminals: { [key: string]: FullTerminal } = {};
  @observable private orderedTerminalIds: string[] = [];
  @observable waitingForNewTerminal = false;

  @computed
  get orderedTerminals() {
    return this.orderedTerminalIds.map(id => this.terminals[id]);
  }

  @action
  handleRemoveTerminal(id: string) {
    let terminal = this.terminals[id];
    if (terminal) {
      this.orderedTerminalIds = this.orderedTerminalIds.filter(x => x !== id);
      delete this.terminals[id];
    }
  }

  @action
  handleExpectTerminal() {
    this.waitingForNewTerminal = true;
    setTimeout(() => {
      if (this.waitingForNewTerminal) {
        this.waitingForNewTerminal = false;
        messageActions.message({
          style: 'danger',
          text: 'Terminal creation timed out.',
        });
      }
    }, 10 * 1000);
  }

  @action
  cancelExpectTerminal() {
    this.waitingForNewTerminal = false;
  }

  @action
  handleAddTerminal = (descriptor: TerminalInfo.Terminal) => {
    this.waitingForNewTerminal = false;
    let terminal = createFullTerminal(descriptor);
    this.terminals[descriptor.id] = terminal;
    this.orderedTerminalIds.push(descriptor.id);
  };

  @action
  handleOutput = (out: TerminalInfo.Output) => {
    const { terminalId, correlationId, output, sensitive, endOfCommand } = out;
    const terminal = this.terminals[terminalId];

    if (!terminal) return;

    const lines = terminal.lines;
    terminal.lines.push({ correlationId, output, sensitive });
    terminal.endOfCommand = endOfCommand;
    terminal.descriptor.currentPath = out.currentPath;

    if (endOfCommand && lines.length > LineCountLimit) {
      terminal.lines = lines.slice(lines.length - LineCountLimit);
    }
  };

  @action
  handleSendCommand = (content: TerminalInfo.SendCommand) => {
    const { terminalId, endOfCommand } = content;
    const terminal = this.terminals[terminalId];

    if (!terminal) return;

    if (endOfCommand) {
      terminal.history.push(terminal.currentCommand);
      terminal.currentHistoryIndex = terminal.history.length;
    }
    terminal.currentCommand = '';
  };

  @action
  handleChangeCommand = ([id, command]: [string, string]) => {
    const terminal = this.terminals[id];

    if (!terminal) return;

    terminal.currentCommand = command;
  };

  @action
  handleGoBack = (id: string) => {
    const terminal = this.terminals[id];

    if (!terminal) return;
    if (terminal.currentHistoryIndex <= 0) return;

    terminal.currentHistoryIndex--;
    terminal.currentCommand = terminal.history[terminal.currentHistoryIndex];
  };

  @action
  handleGoForward = (id: string) => {
    const terminal = this.terminals[id];

    if (!terminal) return;
    if (terminal.currentHistoryIndex >= terminal.history.length) return;

    terminal.currentHistoryIndex++;

    if (terminal.currentHistoryIndex === terminal.history.length) {
      terminal.currentCommand = '';
    } else {
      terminal.currentCommand = terminal.history[terminal.currentHistoryIndex];
    }
  };

  @action
  handleResetAutocomplete(id: string) {
    let terminal = this.terminals[id];

    if (!terminal) {
      return;
    }

    terminal.currentAutocompleteCommand = '';
  }

  @action
  handleAutocomplete(autocompleteData: TerminalInfo.AutocompleteData) {
    let terminal = this.terminals[autocompleteData.terminalId];
    if (!terminal) {
      return;
    }

    terminal.currentCommand = autocompleteData.completionText;
  }
}

export const terminalStore = new TerminalStore();
